import { useEffect, useRef, useState } from "react";
import { BANGALORE_ATTRACTIONS } from "@/mock";
import { ChevronLeft, ChevronRight, MapPin, ExternalLink, Sparkles } from "lucide-react";

const Attractions = () => {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current;
    const cardW = el.querySelector("[data-card]")?.offsetWidth || 320;
    el.scrollBy({ left: dir * (cardW + 16), behavior: "smooth" });
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardW = el.querySelector("[data-card]")?.offsetWidth || 320;
      setActiveIdx(Math.round(el.scrollLeft / (cardW + 16)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="explore"
      className="py-20 md:py-28 px-5 md:px-10 bg-[#F9F7F3] relative overflow-hidden"
      data-testid="attractions-section"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#1A1A1A 1px, transparent 1px), linear-gradient(90deg, #1A1A1A 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-[#C8A97E]" />
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#C8A97E] font-semibold">
                Explore the Region
              </span>
            </div>
            <h2 className="font-serif text-[40px] sm:text-[46px] font-semibold text-[#1A1A1A] leading-[1.1]">
              Famous places, <em className="text-[#C8A97E] font-normal">minutes away</em>
            </h2>
            <p className="text-[15px] text-[#666] max-w-xl mt-3 leading-relaxed">
              From Bangalore's iconic malls and temples to day trips to Mysore, Nandi Hills and Hampi — all within easy reach from Hitha Residency.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-[#E5E0D8] bg-white hover:bg-[#FAF3E7] hover:border-[#C8A97E] flex items-center justify-center transition-all"
              data-testid="attractions-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4 text-[#1A1A1A]" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-[#E5E0D8] bg-white hover:bg-[#FAF3E7] hover:border-[#C8A97E] flex items-center justify-center transition-all"
              data-testid="attractions-next"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4 text-[#1A1A1A]" />
            </button>
          </div>
        </div>

        {/* Desktop: horizontal scroller with snap */}
        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 md:-mx-0 px-5 md:px-0 scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          data-testid="attractions-scroller"
        >
          {BANGALORE_ATTRACTIONS.map((a, i) => (
            <a
              key={a.name}
              href={`https://maps.google.com/?q=${a.maps}`}
              target="_blank"
              rel="noopener noreferrer"
              data-card
              data-testid={`attraction-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex-shrink-0 snap-start w-[280px] sm:w-[320px] lg:w-[340px] relative rounded-[18px] overflow-hidden bg-[#1A1A1A] border border-[#E5E0D8] hover:-translate-y-1 transition-all duration-500 shadow-[0_6px_22px_rgba(0,0,0,0.06)] hover:shadow-[0_14px_40px_rgba(0,0,0,0.12)]"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={a.url}
                  alt={a.name}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.classList.add("bg-gradient-to-br", "from-[#1A1A1A]", "to-[#2A2A2A]");
                  }}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="inline-block bg-[#C8A97E]/20 backdrop-blur-md border border-[#C8A97E]/50 text-[#FAF3E7] text-[9px] font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full">
                    {a.tag}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-md border border-white/20 text-white text-[11px] font-semibold px-3 py-1 rounded-full">
                    <MapPin className="w-3 h-3" /> {a.distance}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="font-serif text-[22px] font-semibold text-white leading-tight mb-1">
                    {a.name}
                  </div>
                  <div className="text-[12px] text-white/70 leading-snug mb-3">
                    {a.subtitle}
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-[#C8A97E] font-semibold tracking-wide opacity-0 group-hover:opacity-100 transition-opacity">
                    View on Maps <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 mt-6">
          {BANGALORE_ATTRACTIONS.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === activeIdx ? "w-8 bg-[#C8A97E]" : "w-1.5 bg-[#E5E0D8]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Attractions;
